/**
 * The small controls of the text creation flow (handoff §0.3): primary,
 * secondary, outline and ghost pills, the switch, the eyebrow and the info
 * banner. Colours are tokens only; one primary per screen (Wave 3 §2).
 */
import type { ButtonHTMLAttributes, CSSProperties, ReactNode } from 'react'
import { CONTROL_SURFACE } from '../../utils/controlSurface'

const base: CSSProperties = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
  borderRadius: 'var(--radius-pill)', cursor: 'pointer', fontFamily: 'var(--font-ui)',
  fontWeight: 600, border: 'none', whiteSpace: 'nowrap',
}

type BtnProps = ButtonHTMLAttributes<HTMLButtonElement> & { full?: boolean; height?: number }

// Every control here is a control, never selectable text: no OS selection on a
// long-press, and no Touch to Search on a tap, a disabled button's included
// (index.css, `.cs-ui`).
const controlClass = (className?: string) => (className ? `${CONTROL_SURFACE} ${className}` : CONTROL_SURFACE)

export function PrimaryButton({ full, height = 52, style, className, disabled, ...rest }: BtnProps) {
  return (
    <button
      type="button"
      className={controlClass(className)}
      disabled={disabled}
      style={{
        ...base, height, padding: '0 28px', fontSize: 16, width: full ? '100%' : undefined,
        background: disabled ? 'var(--bg-secondary)' : 'var(--accent-fill)',
        color: disabled ? 'var(--text-tertiary)' : 'var(--text-on-accent)',
        cursor: disabled ? 'default' : 'pointer', ...style,
      }}
      {...rest}
    />
  )
}

export function SecondaryPill({ full, height = 44, style, className, ...rest }: BtnProps) {
  return (
    <button
      type="button"
      className={controlClass(className)}
      style={{
        ...base, height, padding: '0 16px', fontSize: 14, width: full ? '100%' : undefined,
        background: 'var(--bg-secondary)', color: 'var(--text-primary)', ...style,
      }}
      {...rest}
    />
  )
}

export function OutlinePill({ style, className, ...rest }: BtnProps) {
  return (
    <button
      type="button"
      className={controlClass(className)}
      style={{
        ...base, height: 36, padding: '0 14px', fontSize: 13,
        background: 'var(--bg-surface)', color: 'var(--accent)', border: '1px solid var(--border)', ...style,
      }}
      {...rest}
    />
  )
}

export function GhostButton({ style, className, ...rest }: BtnProps) {
  return (
    <button
      type="button"
      className={controlClass(className)}
      style={{
        ...base, height: 44, padding: '0 8px', fontSize: 14, background: 'transparent',
        color: 'var(--accent)', ...style,
      }}
      {...rest}
    />
  )
}

export function Eyebrow({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return (
    <p
      style={{
        margin: 0, fontSize: 11, fontWeight: 600, letterSpacing: '0.1em',
        textTransform: 'uppercase', color: 'var(--text-tertiary)', ...style,
      }}
    >
      {children}
    </p>
  )
}

/** 46×28 pill: on = accent-fill track with the knob right, off = border track with it left. */
export function Switch({
  checked, onChange, disabled, label, labelledBy,
}: {
  checked: boolean
  onChange: (on: boolean) => void
  disabled?: boolean
  label?: string
  labelledBy?: string
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      aria-labelledby={labelledBy}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      style={{
        width: 46, height: 28, flexShrink: 0, borderRadius: 14, border: 'none', padding: 0,
        position: 'relative', cursor: disabled ? 'default' : 'pointer',
        background: checked ? 'var(--accent-fill)' : 'var(--border)',
        opacity: disabled ? 0.7 : 1, transition: 'background .15s',
      }}
    >
      <span
        aria-hidden
        style={{
          position: 'absolute', top: 4, left: checked ? 22 : 4, width: 20, height: 20,
          borderRadius: '50%', background: checked ? 'var(--text-on-accent)' : 'var(--bg-surface)',
          transition: 'left .15s',
        }}
      />
    </button>
  )
}

/** An info panel: accent tint, radius 16 (handoff §16.1); `tone="notice"` is the same shape in the warning tint. */
export function InfoPanel({
  icon, title, children, tone = 'info', testId,
}: {
  icon: ReactNode
  title: string
  children: ReactNode
  tone?: 'info' | 'notice'
  testId?: string
}) {
  return (
    <div
      role="status"
      data-testid={testId}
      style={{
        display: 'flex', gap: 10, padding: 14, borderRadius: 16,
        background: tone === 'info' ? 'var(--accent-soft)' : 'var(--warning-soft)',
        color: 'var(--text-primary)',
      }}
    >
      <span aria-hidden style={{ flexShrink: 0, marginTop: 1, color: tone === 'info' ? 'var(--accent)' : 'var(--warning-text)' }}>
        {icon}
      </span>
      <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <p style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>{title}</p>
        {children}
      </div>
    </div>
  )
}

/** The 20 px radio of the destination card (handoff §6.1): accent ring and dot when on. */
export function Radio({ on }: { on: boolean }) {
  return (
    <span
      aria-hidden
      style={{
        width: 20, height: 20, flexShrink: 0, borderRadius: '50%', boxSizing: 'border-box',
        border: `2px solid ${on ? 'var(--accent)' : 'var(--border)'}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      {on && <span style={{ width: 10, height: 10, borderRadius: '50%', background: 'var(--accent)' }} />}
    </span>
  )
}

/** A 32 px word chip: the waiting words of §16.1. */
export function WordChip({ children }: { children: ReactNode }) {
  return (
    <span
      style={{
        display: 'inline-flex', alignItems: 'center', height: 32, padding: '0 12px', borderRadius: 16,
        border: '1px solid var(--border)', background: 'var(--bg-surface)',
        fontFamily: 'var(--font-content)', fontSize: 15, color: 'var(--text-primary)',
      }}
    >
      {children}
    </span>
  )
}

/**
 * Tap-only segmented switch (handoff §0.3): never a swipe target. The selected
 * segment is the chrome plane with its border, which carries the selection in dark.
 */
export function Segmented<T extends string>({
  options, value, onChange, label, height = 44, width, inset = 4, fit = false,
}: {
  options: { id: T; label: string; testId?: string; disabled?: boolean }[]
  /** null: no option selected. */
  value: T | null
  onChange: (id: T) => void
  label: string
  height?: number
  width?: number
  /** Track padding and gap: 4 for the view switch, 3 for the role radiogroup. */
  inset?: number
  /**
   * Size each option to its label instead of equal thirds, with a little less padding:
   * a long label («Карточка») never overflows its pill however narrow the row is. The
   * caller lets the control wrap rather than shrink (see MarkPanel's role row).
   */
  fit?: boolean
}) {
  return (
    <div
      role="radiogroup"
      className={CONTROL_SURFACE}
      aria-label={label}
      style={{
        display: 'flex', gap: inset, padding: inset, height, boxSizing: 'border-box', width,
        borderRadius: 'var(--radius-pill)', background: 'var(--bg-secondary)', flexShrink: 0,
      }}
    >
      {options.map((o) => {
        const on = value !== null && o.id === value
        return (
          <button
            key={o.id}
            type="button"
            role="radio"
            aria-checked={on}
            data-testid={o.testId}
            disabled={o.disabled}
            onClick={() => !o.disabled && onChange(o.id)}
            style={{
              opacity: o.disabled ? 0.45 : 1, cursor: o.disabled ? 'not-allowed' : 'pointer',
              flex: fit ? '1 1 auto' : 1, minWidth: 0, borderRadius: 'var(--radius-pill)',
              padding: fit ? '0 10px' : '0 12px',
              fontFamily: 'var(--font-ui)', fontSize: 14, whiteSpace: 'nowrap',
              fontWeight: on ? 600 : 500, color: on ? 'var(--text-primary)' : 'var(--text-secondary)',
              background: on ? 'var(--bg-chrome)' : 'transparent',
              border: on ? '1px solid var(--border-chrome)' : '1px solid transparent',
              boxShadow: on ? 'var(--shadow-card)' : 'none',
            }}
          >
            {o.label}
          </button>
        )
      })}
    </div>
  )
}
